import { Alert, Button, Link, Modal, ModalBody, ModalFooter, ModalHeader, Text } from '@change/ds-core-ui';
import { ErrorMessage } from '@change/ds-forms';
import { TrashIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import { Translate, useI18n } from '@change-corgi/core/react/i18n';
import { Loader } from '@change-corgi/design-system-legacy/components/progressiveDisclosure';

import { useRemoveOption } from './hooks/useRemoveOption';

export const RemoveOption = ({ petitionId, petitionOwnerId }: { petitionId: string; petitionOwnerId: string }) => {
	const {
		data: { isLoading, isSuccess, errorMessage, userCanRemoveThePetition },
		actions: { removePetitionFromMovement, resetModal },
	} = useRemoveOption({ petitionId, petitionOwnerId });
	const { translate } = useI18n();

	if (!userCanRemoveThePetition) {
		return <div className="h-6 w-full" />;
	}

	return (
		<Modal
			trigger={
				<Link
					as="button"
					variant="primary"
					leadingIcon={<TrashIcon />}
					disabled={isLoading}
					className="text-typography-brand active:text-typography-error hover:text-typography-error hover:active:text-typography-error focus:text-typography-error"
					data-testid="movement-remove-petition-button"
					data-qa="movement-remove-petition-button"
				>
					<Text size="small">
						<Translate value="corgi.movement.show.petition_card.remove_petition_link" />
					</Text>
				</Link>
			}
			onClose={resetModal}
		>
			<ModalHeader title={translate('fe.components.remove_movement_petition.title')} />

			<ModalBody description={translate('fe.components.remove_movement_petition.desc')}>
				{errorMessage && (
					<ErrorMessage className="w-full" size="large">
						<Translate value="fe.components.remove_movement_petition.error_message" /> {errorMessage}
					</ErrorMessage>
				)}
				{isSuccess && (
					<Alert variant="success" className="w-full">
						<Translate value="fe.components.remove_movement_petition.success_message" />
					</Alert>
				)}
			</ModalBody>

			<ModalFooter>
				{({ close }) => (
					<>
						<Button
							variant="primary"
							onClick={async () => removePetitionFromMovement({ close })}
							data-qa="modal-movement-remove-petition-button-ok"
							disabled={isLoading}
							className="relative"
						>
							<Text className={cn({ 'inline-flex md:pl-6': isLoading })} fontWeight="bold">
								{isLoading && <Loader size={24} data-testid="spinner" className="absolute top-3 left-2" />}
								<Translate value="fe.components.confirm_modal.buttons.delete" />
							</Text>
						</Button>
						<Button
							variant="secondary"
							onClick={() => {
								resetModal();
								close();
							}}
							data-qa="modal-movement-remove-petition-button-cancel"
							disabled={isLoading}
						>
							<Translate value="fe.components.confirm_modal.buttons.cancel" />
						</Button>
					</>
				)}
			</ModalFooter>
		</Modal>
	);
};
